import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldWarning, Plus, X, Trash, Upload, ArrowLeft,
  MapPin, QrCode, LinkSimple, Sparkle, ArrowsOut, ImageSquare,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const CATS_CANONICAL = [
  "water_main", "gas_shutoff", "electrical_panel", "gfi_outlet",
  "kitchen_sink", "bathroom_sink", "toilet_shutoff", "washer_shutoff",
  "hvac_switch", "hvac_filter",
  "fire_extinguisher", "smoke_detector", "co_detector", "custom",
];

function loadPannellum() {
  if (window.pannellum) return Promise.resolve();
  return new Promise((res) => {
    if (!document.querySelector('link[data-pannellum]')) {
      const l = document.createElement("link");
      l.rel = "stylesheet"; l.dataset.pannellum = "1";
      l.href = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css";
      document.head.appendChild(l);
    }
    if (!document.querySelector('script[data-pannellum]')) {
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js";
      s.dataset.pannellum = "1"; s.onload = res;
      document.head.appendChild(s);
    } else res();
  });
}

export default function EmergencyMap() {
  const [sp] = useSearchParams();
  const address = sp.get("address") || "";
  const unit = sp.get("unit") || "";
  const [map, setMap] = useState(null);
  const [activePhotoId, setActivePhotoId] = useState("");
  const [placingTag, setPlacingTag] = useState(null); // {label, category, instruction_text}
  const [showTagForm, setShowTagForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/emergency-map?address=${encodeURIComponent(address)}&unit=${encodeURIComponent(unit)}`);
      setMap(r.data);
      if (!activePhotoId && r.data.photos?.[0]) setActivePhotoId(r.data.photos[0].id);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { if (address) load(); /* eslint-disable-next-line */ }, [address, unit]);

  if (!address) {
    return <AdminShell><div className="p-6">
      <h1 className="text-2xl font-black">Emergency Map</h1>
      <p className="text-sm text-slate-500 mt-2">Provide <code>?address=…&unit=…</code> to open a specific unit's map.
        <br/>Or go to <Link to="/admin/properties" className="text-cyan-700 hover:underline">Properties</Link> and click Emergency Map on a unit.</p>
    </div></AdminShell>;
  }
  if (!map) return <AdminShell><div className="p-6 text-slate-500">Loading…</div></AdminShell>;

  const activePhoto = map.photos.find(p => p.id === activePhotoId) || map.photos[0];
  const tagsOnActive = map.tags.filter(t => t.photo_id === (activePhoto?.id || ""));
  const untaggedTags = map.tags.filter(t => !t.photo_id);

  const savePreset = async () => {
    try {
      const r = await api.post(`/emergency-map/${map.id}/preset`, {});
      toast.success(`${r.data.added} standard shut-offs added`); load();
    } catch { toast.error("Failed"); }
  };
  const uploadPhoto = async (file, type) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("photo_type", type);
    try {
      const r = await api.post(`/emergency-map/${map.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Uploaded"); await load(); setActivePhotoId(r.data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
  };
  const deletePhoto = async (pid) => {
    if (!window.confirm("Delete this photo? Tags on it will lose their pin.")) return;
    try { await api.delete(`/emergency-map/${map.id}/photos/${pid}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const saveMatterport = async (url) => {
    try { await api.patch(`/emergency-map/${map.id}`, { matterport_url: url, notes: map.notes || "" }); toast.success("Saved"); load(); }
    catch { toast.error("Failed"); }
  };
  const deleteTag = async (tid) => {
    if (!window.confirm("Delete tag?")) return;
    try { await api.delete(`/emergency-map/${map.id}/tags/${tid}`); load(); }
    catch { toast.error("Failed"); }
  };
  const rotate = async () => {
    if (!window.confirm("Rotate the public link? Anyone with the old link (or old QR sticker) will lose access.")) return;
    try { await api.post(`/emergency-map/${map.id}/rotate-token`, {}); toast.success("Link rotated"); load(); }
    catch { toast.error("Failed"); }
  };
  const copyPublicLink = () => {
    const url = `${window.location.origin}/emergency/${map.public_token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.info(url));
  };
  const downloadQr = () => {
    window.open(`${API}/api/emergency-map/${map.id}/qr.png`, "_blank");
  };
  const downloadLabels = () => {
    window.open(`${API}/api/emergency-map/${map.id}/labels.pdf`, "_blank");
  };
  const downloadMoveInKit = () => {
    const qs = `address=${encodeURIComponent(address)}&unit=${encodeURIComponent(unit)}`;
    window.open(`${API}/api/emergency-map/move-in-kit.pdf?${qs}`, "_blank");
    toast.success("Move-In Kit generating — presets added if missing.");
    setTimeout(load, 1200);
  };
  const toggleApplied = async (tagId, applied) => {
    try { await api.post(`/emergency-map/${map.id}/tags/${tagId}/apply`, { applied }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="emergency-map-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Link to="/admin/properties" className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ArrowLeft size={12}/> Back to Properties
            </Link>
            <h1 className="text-3xl md:text-4xl font-black uppercase tracking-widest flex items-center gap-2 mt-1">
              <ShieldWarning weight="fill" className="text-red-500"/> Emergency Map
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              <b>{address}</b>{unit && ` · Unit ${unit}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={copyPublicLink} data-testid="copy-public-link" className="h-9 px-3 border-2 border-slate-900 bg-white hover:bg-slate-100 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
              <LinkSimple size={12}/> Copy Public Link
            </button>
            <button onClick={downloadQr} data-testid="download-qr" className="h-9 px-3 bg-slate-900 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 shadow-[3px_3px_0_#334155]">
              <QrCode size={12}/> QR Sticker
            </button>
            <button onClick={downloadLabels} data-testid="download-labels" className="h-9 px-3 bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 shadow-[3px_3px_0_#0f172a]" disabled={map.tags.length === 0} title="Avery-style sticker sheet, one per tag">
              <QrCode size={12}/> Print Labels
            </button>
            <button onClick={downloadMoveInKit} data-testid="download-move-in-kit"
                    className="h-9 px-3 bg-emerald-600 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 shadow-[3px_3px_0_#0f172a]"
                    title="One-tap move-in starter pack — seeds standard shut-offs (if missing) and prints all sticker labels">
              🎁 Move-In Kit
            </button>
            <button onClick={rotate} className="h-9 w-9 border-2 border-slate-400 text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center" title="Rotate public link">
              <ArrowsOut size={14}/>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* LEFT — Tag list */}
          <div className="lg:col-span-2 space-y-3">
            <div className="rugged-card p-3 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black uppercase tracking-widest">Shut-offs & Safety</h3>
                <span className="text-[10px] text-slate-500">{map.tags.length} tags</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={savePreset} data-testid="preset-btn"
                  className="h-8 px-2 bg-emerald-600 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1 shadow-[2px_2px_0_#0f172a]">
                  <Sparkle size={10} weight="fill"/> Add Standard Shut-offs
                </button>
                <button onClick={() => { setEditingTag(null); setShowTagForm(true); }} data-testid="add-tag-btn"
                  className="h-8 px-2 bg-red-600 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1 shadow-[2px_2px_0_#0f172a]">
                  <Plus size={10}/> Custom Tag
                </button>
              </div>
            </div>
            {map.tags.length === 0 ? (
              <div className="rugged-card p-4 text-center text-xs text-slate-500">
                No shut-offs tagged yet. Click <b>Add Standard Shut-offs</b> to prefill six common ones.
              </div>
            ) : (
              <div className="space-y-1.5">
                {map.tags.map(t => (
                  <TagRow key={t.id} tag={t} active={t.photo_id === activePhoto?.id}
                    onEdit={() => { setEditingTag(t); setShowTagForm(true); }}
                    onDelete={() => deleteTag(t.id)}
                    onFocus={() => { if (t.photo_id) setActivePhotoId(t.photo_id); }}
                    onToggleApplied={(v) => toggleApplied(t.id, v)}/>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — Viewer */}
          <div className="lg:col-span-3 space-y-3">
            <PhotoUploaderRow onUpload={uploadPhoto} photos={map.photos} onSelect={setActivePhotoId} activeId={activePhotoId} onDelete={deletePhoto}/>
            <MatterportField initial={map.matterport_url} onSave={saveMatterport}/>
            <ViewerCard photo={activePhoto} mapId={map.id} tags={tagsOnActive}
              onPlacePin={async (coords, tagId) => {
                await api.patch(`/emergency-map/${map.id}/tags/${tagId}`, { ...coords, photo_id: activePhoto.id });
                load();
              }}
              placingTag={placingTag} setPlacingTag={setPlacingTag}/>
            {map.matterport_url && (
              <div className="rugged-card p-0 overflow-hidden">
                <div className="p-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest">Matterport tour</div>
                <div className="aspect-video bg-black">
                  <iframe src={map.matterport_url} title="Matterport" allowFullScreen
                          allow="xr-spatial-tracking" className="w-full h-full border-0"/>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showTagForm && (
        <TagFormModal
          existing={editingTag}
          mapId={map.id}
          onClose={() => { setShowTagForm(false); setEditingTag(null); }}
          onSaved={() => { setShowTagForm(false); setEditingTag(null); load(); }}/>
      )}
    </AdminShell>
  );
}

function TagRow({ tag, active, onEdit, onDelete, onFocus, onToggleApplied }) {
  return (
    <div className={`p-2 border-2 flex items-start gap-2 ${active ? "border-red-500 bg-red-50" : "border-slate-200 bg-white"}`} data-testid={`tag-${tag.id}`}>
      <button onClick={onFocus} className="w-9 h-9 shrink-0 flex items-center justify-center text-lg border-2"
              style={{ borderColor: tag.color || "#0f172a" }}>
        <span>{tag.icon || "📍"}</span>
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="font-black text-sm">{tag.label}</span>
          {tag.short_id && (
            <span className="px-1.5 h-4 leading-4 bg-slate-900 text-white font-black tracking-widest text-[9px]"
                  data-testid={`tag-short-${tag.short_id}`}>
              {tag.short_id}
            </span>
          )}
          {tag.applied_at && (
            <span className="px-1.5 h-4 leading-4 bg-emerald-600 text-white font-black uppercase tracking-widest text-[9px]">
              🏷️ tagged
            </span>
          )}
        </div>
        {tag.instruction_text && <div className="text-[11px] text-slate-600 leading-snug">{tag.instruction_text}</div>}
        <label className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
          <input type="checkbox" checked={!!tag.applied_at}
                 onChange={e => onToggleApplied(e.target.checked)}
                 data-testid={`applied-check-${tag.id}`}/>
          Applied on-site {tag.applied_by_name && `by ${tag.applied_by_name}`}
        </label>
      </div>
      <button onClick={onEdit} className="h-7 px-2 border-2 border-slate-300 text-[9px] font-black uppercase tracking-widest hover:bg-slate-50">Edit</button>
      <button onClick={onDelete} className="h-7 w-7 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
        <Trash size={12}/>
      </button>
    </div>
  );
}

function PhotoUploaderRow({ onUpload, photos, onSelect, activeId, onDelete }) {
  const stdRef = useRef(), panRef = useRef();
  return (
    <div className="rugged-card p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Photos</span>
        <input ref={stdRef} type="file" accept="image/*" hidden onChange={e => onUpload(e.target.files[0], "standard")}/>
        <input ref={panRef} type="file" accept="image/jpeg,image/png" hidden onChange={e => onUpload(e.target.files[0], "panorama_360")}/>
        <button onClick={() => stdRef.current?.click()} data-testid="upload-standard"
          className="h-8 px-2 bg-cyan-700 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
          <Upload size={10}/> Upload Photo
        </button>
        <button onClick={() => panRef.current?.click()} data-testid="upload-360"
          className="h-8 px-2 bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
          <ImageSquare size={10}/> Upload 360°
        </button>
      </div>
      {photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto">
          {photos.map(p => (
            <button key={p.id} onClick={() => onSelect(p.id)}
              className={`shrink-0 relative w-24 h-16 border-2 overflow-hidden ${activeId === p.id ? "border-red-500 shadow-[3px_3px_0_#0f172a]" : "border-slate-300"}`}>
              <img src={`${API}/api/emergency-map/${p.storage_path?.split("/")[3] || ""}/photo/${p.id}`}
                   alt="" className="w-full h-full object-cover" onError={e => e.currentTarget.style.display = "none"}/>
              <div className="absolute top-0 left-0 px-1 text-[7px] font-black uppercase tracking-widest bg-slate-900 text-white">
                {p.type === "panorama_360" ? "360°" : "2D"}
              </div>
              <button onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
                className="absolute top-0 right-0 w-4 h-4 bg-red-600 text-white text-[9px] leading-none">×</button>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MatterportField({ initial, onSave }) {
  const [v, setV] = useState(initial || "");
  useEffect(() => setV(initial || ""), [initial]);
  return (
    <div className="rugged-card p-3 space-y-1.5">
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Matterport embed URL (optional)</label>
      <div className="flex gap-2">
        <input value={v} onChange={e => setV(e.target.value)} data-testid="matterport-input"
          placeholder="https://my.matterport.com/show/?m=xxxxx"
          className="flex-1 h-10 px-2 border-2 border-slate-300 text-xs"/>
        <button onClick={() => onSave(v)} data-testid="matterport-save"
          className="h-10 px-3 bg-slate-900 text-white font-black uppercase tracking-widest text-[10px]">Save</button>
      </div>
    </div>
  );
}

function ViewerCard({ photo, mapId, tags, onPlacePin, placingTag, setPlacingTag }) {
  const imgRef = useRef(), pannRef = useRef(), viewerRef = useRef();

  useEffect(() => {
    if (!photo || photo.type !== "panorama_360" || !pannRef.current) return;
    let alive = true;
    loadPannellum().then(() => {
      if (!alive || !window.pannellum) return;
      if (viewerRef.current) { try { viewerRef.current.destroy(); } catch { /* noop */ } }
      viewerRef.current = window.pannellum.viewer(pannRef.current, {
        type: "equirectangular",
        panorama: `${API}/api/emergency-map/${mapId}/photo/${photo.id}`,
        autoLoad: true, showControls: true, showZoomCtrl: true,
        hotSpots: tags.filter(t => t.photo_id === photo.id).map(t => ({
          pitch: t.pitch || 0, yaw: t.yaw || 0, type: "info",
          text: `${t.icon || "📍"}  ${t.label}${t.instruction_text ? " — " + t.instruction_text : ""}`,
        })),
      });
    });
    return () => { alive = false; try { viewerRef.current?.destroy(); } catch { /* noop */ } };
  }, [photo, tags, mapId]);

  if (!photo) return (
    <div className="rugged-card p-6 text-center text-sm text-slate-500">
      Upload a photo above, or work from just the tag list — a photo isn't required.
    </div>
  );

  const handleClick = (e) => {
    if (!placingTag) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x_pct = ((e.clientX - rect.left) / rect.width) * 100;
    const y_pct = ((e.clientY - rect.top) / rect.height) * 100;
    onPlacePin({ x_pct, y_pct }, placingTag);
    toast.success("Pin placed");
    setPlacingTag(null);
  };

  const untaggedInScope = tags.filter(t => !t.photo_id || t.photo_id === photo.id);
  const noPin = untaggedInScope.filter(t => !t.photo_id || (t.x_pct === 0 && t.y_pct === 0 && t.photo_id !== photo.id));

  return (
    <div className="rugged-card p-0 overflow-hidden">
      <div className="p-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-2 flex-wrap">
        <ImageSquare size={12}/> Viewer
        <span className="opacity-70">{photo.type === "panorama_360" ? "360° pan" : "standard"}</span>
        {placingTag && <span className="ml-auto px-2 py-0.5 bg-red-600">Placing: click on photo</span>}
      </div>
      {photo.type === "panorama_360" ? (
        <div ref={pannRef} className="w-full aspect-video bg-black"/>
      ) : (
        <div className={`relative ${placingTag ? "cursor-crosshair" : ""}`} onClick={handleClick}>
          <img ref={imgRef} src={`${API}/api/emergency-map/${mapId}/photo/${photo.id}`}
               alt="" className="w-full block" data-testid="viewer-photo"/>
          {tags.filter(t => t.photo_id === photo.id && (t.x_pct || t.y_pct)).map(t => (
            <div key={t.id}
              style={{ left: `${t.x_pct}%`, top: `${t.y_pct}%`, backgroundColor: t.color || "#dc2626" }}
              className="absolute -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full text-white flex items-center justify-center text-lg border-4 border-white shadow-lg animate-pulse"
              title={`${t.label} — ${t.instruction_text || ""}`}>
              {t.icon || "📍"}
            </div>
          ))}
        </div>
      )}
      {photo.type !== "panorama_360" && (
        <div className="p-2 bg-slate-50 flex items-center gap-2 flex-wrap text-[10px]">
          <span className="font-black uppercase tracking-widest text-slate-500">Place pin:</span>
          {tags.filter(t => !t.photo_id).map(t => (
            <button key={t.id} onClick={() => setPlacingTag(t.id)} data-testid={`place-${t.id}`}
              className={`h-7 px-2 border-2 font-black uppercase tracking-widest ${placingTag === t.id ? "bg-red-600 text-white border-red-600" : "border-slate-300 bg-white hover:bg-slate-100"}`}>
              {t.icon} {t.label}
            </button>
          ))}
          {tags.filter(t => !t.photo_id).length === 0 && (
            <span className="text-slate-400">All tags already pinned. Click a pin to relocate: {tags.filter(t => t.photo_id === photo.id).map(t => (
              <button key={t.id} onClick={() => setPlacingTag(t.id)} className="mx-0.5 underline">{t.label}</button>
            ))}</span>
          )}
        </div>
      )}
    </div>
  );
}

function TagFormModal({ existing, mapId, onClose, onSaved }) {
  const [f, setF] = useState(existing || {
    label: "", category: "custom", instruction_text: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.label.trim()) return toast.error("Label required");
    setBusy(true);
    try {
      if (existing) {
        await api.patch(`/emergency-map/${mapId}/tags/${existing.id}`, {
          label: f.label, category: f.category, instruction_text: f.instruction_text,
        });
      } else {
        await api.post(`/emergency-map/${mapId}/tags`, f);
      }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="tag-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-red-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">{existing ? "Edit Tag" : "New Tag"}</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2">
          <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} data-testid="tag-category"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm">
            {CATS_CANONICAL.map(c => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
          <input value={f.label} onChange={e => setF({ ...f, label: e.target.value })} data-testid="tag-label"
            placeholder="Label (e.g., Main Water Shut-off)"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <textarea value={f.instruction_text} onChange={e => setF({ ...f, instruction_text: e.target.value })}
            data-testid="tag-instruction" rows={3}
            placeholder="Instruction — e.g. 'Turn clockwise to shut off'"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <button onClick={save} disabled={busy || !f.label.trim()} data-testid="tag-save"
            className="w-full h-11 bg-red-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : (existing ? "Save" : "Add Tag")}
          </button>
        </div>
      </div>
    </div>
  );
}
